// client/src/pages/AdministrationDashboardPage.js
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Container,
  Alert,
  Badge,
  Table,
  Form,
  Button,
  Modal,
  Row,
  Col,
  Card,
  Collapse
} from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { useDashboardData } from '../hooks/useDashboardData';
import { getAuthHeaders } from '../utils/authHelpers';
import '../styles/OrderList.css';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;
const ORDERS_API_URL = `${API_ROOT_URL}/orders`;

export default function AdministrationDashboardPage() {
  const {
    user,
    allOrders, // Now we get the full, unfiltered list
    loading,
    error,
    submissionMessage,
    setSubmissionMessage,
    orderTaskStates,
    setOrderTaskStates,
    getTasksForStage,
    stageMap,
    getStageBadgeVariant,
    formatDate,
    fetchOrdersForRole,
  } = useDashboardData();

  // --- Local State for Search and Filters ---
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOrderType, setFilterOrderType] = useState('All');

  // Local hooks (must be before any returns)
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showAdvanceConfirmModal, setShowAdvanceConfirmModal] = useState(false);
  const [orderToAdvance, setOrderToAdvance] = useState(null);
  const [expandedRows, setExpandedRows] = useState({}); // orderId => boolean
  const topScrollRef = useRef(null);
  const topInnerRef = useRef(null);
  const tableScrollRef = useRef(null);

  // Auto-dismiss submission alerts after 3 seconds
  useEffect(() => {
    if (!submissionMessage.message) return;
    const timer = setTimeout(() => {
      setSubmissionMessage({ message: '', type: '' });
    }, 3000);
    return () => clearTimeout(timer);
  }, [submissionMessage.message, setSubmissionMessage]);

    const tasksForStage = useMemo(() => {
        return getTasksForStage('Administration') || [];
    }, [getTasksForStage]);

    // Build initial snapshot of Administration task states
    const initialTaskStates = useMemo(() => {
    const map = {};

    allOrders.forEach(order => {
        const stageData = order.administration || {};
        const snapshot = {};

        tasksForStage.forEach(task => {
        if (task.type === 'checkbox') {
            snapshot[task.id] = stageData[task.id] || false;
        } 
        else if (task.type === 'date') {
            snapshot[task.id] = stageData[task.id]
            ? new Date(stageData[task.id]).toISOString().split('T')[0]
            : '';
        }
        });

        map[order.orderId] = snapshot;
    });

    return map;
    }, [allOrders, tasksForStage]);


  useEffect(() => {
    if (Object.keys(orderTaskStates).length === 0 && Object.keys(initialTaskStates).length > 0) {
      setOrderTaskStates(initialTaskStates);
    }
  }, [initialTaskStates, orderTaskStates, setOrderTaskStates]);

  // Memoized filter logic for the list of orders
  const filteredOrders = useMemo(() => {
    return allOrders.filter(order => {
      const s = (searchTerm || '').toLowerCase();
      const matchesSearch =
        s === '' ||
        order.orderId?.toLowerCase().includes(s) ||
        order.customerName?.toLowerCase().includes(s) ||
        (user.role !== 'Dealer' && order.dealerName?.toLowerCase().includes(s)) ||
        (order.invoiceNumber && order.invoiceNumber.toLowerCase().includes(s)) ||
        (order.tbcNumber && order.tbcNumber.toLowerCase().includes(s)) ||
        (order.orderType && order.orderType.toLowerCase().includes(s)) ||
        (order.customWork && order.customWork.toLowerCase().includes(s));

      const matchesOrderType = filterOrderType === 'All' || order.orderType === filterOrderType;

      return matchesSearch && matchesOrderType;
    });
  }, [allOrders, searchTerm, filterOrderType, user]);

  const availableOrderTypes = useMemo(() => {
    const types = Array.from(new Set(allOrders.map(o => o.orderType).filter(Boolean))).sort();
    return ['All', ...types];
  }, [allOrders]);

  useEffect(() => {
    if (!topScrollRef.current || !topInnerRef.current || !tableScrollRef.current) return;

    const topScroll = topScrollRef.current;
    const topInner = topInnerRef.current;
    const tableScroll = tableScrollRef.current;

    topInner.style.width = `${tableScroll.scrollWidth}px`;

    const handleTopScroll = () => { tableScroll.scrollLeft = topScroll.scrollLeft; };
    const handleTableScroll = () => { topScroll.scrollLeft = tableScroll.scrollLeft; };

    topScroll.addEventListener('scroll', handleTopScroll);
    tableScroll.addEventListener('scroll', handleTableScroll);

    const resizeObserver = new ResizeObserver(() => {
      topInner.style.width = `${tableScroll.scrollWidth}px`;
    });
    resizeObserver.observe(tableScroll);

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      tableScroll.removeEventListener('scroll', handleTableScroll);
      resizeObserver.disconnect();
    };
  }, [filteredOrders]);

    // Dirty check using normalized tasks
    const isOrderDirty = useCallback((order) => {
        const current = orderTaskStates[order.orderId] || {};
        const initial = initialTaskStates[order.orderId] || {};

        for (const task of tasksForStage) {
            const fallback = task.type === 'checkbox' ? false : '';
            const initVal = (initial[task.id] ?? fallback);
            const curVal = (current[task.id] ?? initVal);
            if (curVal !== initVal) return true;
        }

        return false;
    }, [orderTaskStates, initialTaskStates, tasksForStage]);


  const toggleRow = useCallback((orderId) => {
    setExpandedRows(prev => ({ ...prev, [orderId]: !prev[orderId] }));
  }, []);

  const handleTaskChange = useCallback((orderId, taskId, value) => {
    setOrderTaskStates(prev => {
      const next = { ...prev };
      next[orderId] = { ...(next[orderId] || {}), [taskId]: value };
      return next;
    });
  }, [setOrderTaskStates]);

  const handleCloseSaveConfirmModal = useCallback(() => {
    setShowSaveConfirmModal(false);
  }, []);

  const handleSaveConfirm = useCallback(async () => {
    setShowSaveConfirmModal(false);
    setSubmissionMessage({ message: '', type: '' });

    const dirtyOrders = filteredOrders.filter(order => isOrderDirty(order));

    if (dirtyOrders.length === 0) {
      setSubmissionMessage({ message: 'No changed orders to save.', type: 'warning' });
      return;
    }

    try {
      for (const order of dirtyOrders) {
        const orderId = order.orderId;
        const tasksForOrder = orderTaskStates[orderId];
        if (!tasksForOrder) continue;

        const updatedStageData = {};
        for (const [taskId, val] of Object.entries(tasksForOrder)) {
          const def = tasksForStage.find(t => t.id === taskId);
          if (!def) continue;

          if (def.type === 'date' && val) {
            const [y, m, d] = val.split('-').map(Number);
            updatedStageData[taskId] = new Date(y, m - 1, d);
          } else {
            updatedStageData[taskId] = val;
          }
        }

        const stageKey = stageMap[order.currentStage];
        const payload = {
          [stageKey]: {
            ...(order[stageKey] || {}),
            ...updatedStageData,
          },
          updatedAt: new Date(),
        };

        await fetch(`${ORDERS_API_URL}/${orderId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify(payload),
        });
      }

      await fetchOrdersForRole();
      setSubmissionMessage({
        message: `All changed orders have been saved successfully!`,
        type: 'success',
      });

    } catch (err) {
      setSubmissionMessage({
        message: 'Error saving one or more orders.',
        type: 'danger',
      });
    }
    }, [
      filteredOrders,
      isOrderDirty,
      orderTaskStates,
      tasksForStage,
      stageMap,
      fetchOrdersForRole,
      setSubmissionMessage,
  ]);

  const handleAdvanceStageClick = useCallback((order) => {
    if (isOrderDirty(order)) {
      setSubmissionMessage({
        message: 'You have unsaved changes. Please save them before marking this stage as done.',
        type: 'danger'
      });
      return;
    }

    setOrderToAdvance({
      orderId: order.orderId,
      customerName: order.customerName,
      dealerName: order.dealerName,
      currentStage: order.currentStage,
      shippingMethod: order.shippingMethod
    });

    setShowAdvanceConfirmModal(true);
  }, [isOrderDirty, setSubmissionMessage]);

  const handleCloseAdvanceConfirmModal = useCallback(() => {
    setShowAdvanceConfirmModal(false);
    setOrderToAdvance(null);
  }, []);

  const handleAdvanceStageConfirm = useCallback(async () => {
    if (!orderToAdvance) return;

    setSubmissionMessage({ message: '', type: '' });
    setShowAdvanceConfirmModal(false);

    const { orderId, dealerName, customerName } = orderToAdvance;

    const order = allOrders.find(o => o.orderId === orderId);
    if (!order) {
        setSubmissionMessage({ message: 'Order not found in current view.', type: 'danger' });
        return;
    }

    // Safety guard (optional but recommended)
    if (order.currentStage !== 'Administration') {
        setSubmissionMessage({ message: 'Only Administration orders can be advanced from this page.', type: 'danger' });
        return;
    }

    const nextStage = 'Pre-Production';

    const updatePayload = {
        currentStage: nextStage,
        movedFromAdmin: true,
        administration: {
        ...(order.administration || {}),
        status: 'Completed',
        completionDate: new Date(),
        },
        preProduction: {
        managerName: user?.username || 'System',
        status: 'Pending',
        startDate: new Date(),
        },
    };

    try {
        const res = await fetch(`${ORDERS_API_URL}/${orderId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
        },
        body: JSON.stringify(updatePayload),
        });

        const result = await res.json();

        if (res.ok) {
        await fetchOrdersForRole();
        setSubmissionMessage({
            message: `Order for ${dealerName} - ${customerName} successfully moved to ${nextStage}!`,
            type: 'success',
        });
        } else {
        setSubmissionMessage({
            message: result.message || `Failed to move order ${orderId}.`,
            type: 'danger',
        });
        }
    } catch (e) {
        setSubmissionMessage({
        message: 'Network error or server unreachable while advancing order.',
        type: 'danger',
        });
    } finally {
        setOrderToAdvance(null);
    }
    }, [
    orderToAdvance,
    allOrders,
    user,
    fetchOrdersForRole,
    setSubmissionMessage,
    ]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterOrderType('All');
  };

  const TaskInputRow = ({ order, task, currentOrderTaskState, handleTaskChange, loading, alignDesktop = 'start' // Default to Left on Desktop
  }) => {
    
    // 1. Define Alignment Classes based on the prop
    // Mobile is ALWAYS centered (justify-content-center). 
    // Desktop is dynamic (start vs center).
    const flexClass = alignDesktop === 'center'
      ? "d-flex justify-content-center" 
      : "d-flex justify-content-center justify-content-md-start";

    const textClass = alignDesktop === 'center'
      ? "text-center"
      : "text-center text-md-start";

    return (
      <div className="mb-2 w-100">
        {task.type === 'checkbox' ? (
          <div className={flexClass}>
            <Form.Check 
              type="checkbox"
              id={`task-${order.orderId}-${task.id}`} 
              className="mb-0"
            >
              <Form.Check.Input
                type="checkbox"
                checked={!!currentOrderTaskState[task.id]}
                onChange={(e) => handleTaskChange(order.orderId, task.id, e.target.checked)}
                disabled={loading}
              />
              <Form.Check.Label className="ms-2">{task.label}</Form.Check.Label>
            </Form.Check>
          </div>
        ) : (
          <Form.Group controlId={`task-${order.orderId}-${task.id}`} className="mb-0 w-100">
            {/* Label alignment */}
            <Form.Label className={`mb-1 d-block ${textClass}`}>
              {task.label} {task.required && <span className="text-danger">*</span>}
            </Form.Label>
            
            {/* Input alignment */}
            <div className={flexClass}>
              <Form.Control
                type="date"
                value={currentOrderTaskState[task.id] ?? ''}
                onChange={(e) => handleTaskChange(order.orderId, task.id, e.target.value)}
                disabled={loading}
                className="w-auto" 
              />
            </div>
          </Form.Group>
        )}
      </div>
    );
  };

    // Early returns AFTER hooks
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== 'Admin') { return <Navigate to="/dashboard" replace />; }

  return (
    <Container
      fluid
      style={{
        backgroundColor: '#4595ad29',
        minHeight: '100vh',
        paddingTop: '2rem',
        paddingBottom: '2rem',
        margin: 0
      }}
    >
      <Container className="my-5">
        {/* Welcome */}
        <Row className="justify-content-center">
          <Col md={10}>
            <Card className="shadow-lg p-4 mb-4">
              <Card.Body>
                <h1 className="mb-2 display-6 text-center">Welcome, {user?.username}!</h1>
                <p className="lead text-center mb-0">
                  This is your personalized dashboard for the Tracking System Portal.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}
        {submissionMessage.message && <Alert variant={submissionMessage.type}>{submissionMessage.message}</Alert>}

        {/* Search & Filters */}
        <Row className="mb-4 justify-content-center">
          <Col md={10}>
            <Card className="shadow-sm p-3">
              <Row className="align-items-center">
                <Col md={8} className="mb-3 mb-md-0">
                  <Form.Group controlId="searchOrders">
                    <Form.Label>Search Orders</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search by Order ID, Customer, Dealer, Invoice, or Order Type"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      disabled={loading}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="filterOrderType">
                    <Form.Label>Order Type</Form.Label>
                    <Form.Select
                      value={filterOrderType}
                      onChange={(e) => setFilterOrderType(e.target.value)}
                      disabled={loading}
                    >
                      {availableOrderTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col className="text-end">
                  <Button variant="outline-secondary" onClick={clearFilters} disabled={loading}>
                    Clear Filters
                  </Button>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <h3 className="mb-3 text-center">Pending Orders Backlog</h3>

        <div className="d-flex flex-wrap justify-content-center gap-3 mb-3 mt-1">
          <Button
            variant={
              loading || Object.values(filteredOrders).every(order => !isOrderDirty(order))
                ? "secondary"
                : "success"
            }
            size="md"
            disabled={loading || Object.values(filteredOrders).every(order => !isOrderDirty(order))}
            onClick={() => setShowSaveConfirmModal(true)}
          >
            Save All Changed Orders
          </Button>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center my-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          filteredOrders.length === 0 ? (
            <Alert>No orders found.</Alert>
          ) : (
            <div className="table-scroll-top-wrapper">
              {/* Top scrollbar */}
              <div className="top-scroll" ref={topScrollRef}>
                <div className="top-scroll-inner" ref={topInnerRef} />
              </div>

              {/* Actual scrollable table */}
              <div className="table-responsive" ref={tableScrollRef}>
                <Table bordered hover className="shadow-sm rounded order-list-table" style={{ minWidth: '1100px' }}>
                  <thead className="table-dark">
                    <tr>
                      <th>Order ID</th>
                      <th>Invoice Number</th>
                      <th>TBC Number</th>
                      <th>Order Type</th>
                      <th>Current Stage</th>
                      <th>Dealer Name</th>
                      <th>Customer Name</th>
                      <th>Custom Work</th>
                      <th>Shipping Method</th>
                      <th>Order Date</th>
                      <th style={{ width: 220 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map(order => {
                      const tasksForCurrentStage = tasksForStage;
                      const currentOrderTaskState = orderTaskStates[order.orderId] || {};

                      return (
                        <React.Fragment key={order.orderId}>
                          <tr>
                            <td>{order.orderId}</td>
                            <td>{order.invoiceNumber || 'N/A'}</td>
                            <td>{order.tbcNumber || 'N/A'}</td>
                            <td>{order.orderType || 'N/A'}</td>
                            <td><Badge bg={getStageBadgeVariant(order.currentStage)}>{order.currentStage}</Badge></td>
                            <td>{order.dealerName}</td>
                            <td>{order.customerName}</td>
                            <td>{order.customWork || 'None'}</td>
                            <td>{order.shippingMethod || 'N/A'}</td>
                            <td>{formatDate(order.orderDate, false)}</td>
                            <td className="align-top">
                              <div className="d-flex flex-column gap-2">
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleAdvanceStageClick(order)}
                                  disabled={loading}
                                >
                                  Mark as Done & Send
                                </Button>

                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => toggleRow(order.orderId)}
                                  aria-expanded={!!expandedRows[order.orderId]}
                                >
                                  {expandedRows[order.orderId] ? 'Hide Tasks' : 'View Tasks'}
                                </Button>
                              </div>
                            </td>
                          </tr>

                          {/* Expanded tasks row */}
                          <tr>
                            <td colSpan="11" className="p-0">
                              <Collapse in={!!expandedRows[order.orderId]}>
                                <div className="p-3 bg-light border-top">
                                  {tasksForCurrentStage.length > 0 ? (
                                    <Row className="gx-4">
                                        {/* Column 1: Info Box */}
                                        <Col md={6}>
                                            <Card className="shadow-sm">
                                            <Card.Body>
                                                <h6 className="mb-2 fw-bold text-center">
                                                ** Note for Reskin / Remod / Repair Orders **
                                                </h6>

                                                <div className="small text-start">
                                                <p className="mb-2">
                                                    The following additional steps must be completed <strong>prior to creating the invoice</strong>:
                                                </p>

                                                <ul className="mb-0 ps-3 text-start">
                                                    <li>Determine if requested changes are possible</li>
                                                    <li>Deliver copy of specifications to Sewing Supervisor</li>
                                                    <li>Perform frame & components calculations</li>
                                                    <li>Deliver calculations for invoice creation</li>
                                                </ul>
                                                </div>
                                            </Card.Body>
                                            </Card>
                                        </Col>

                                        {/* Column 2: Administration Tasks */}
                                        <Col md={6}>
                                        <h6 className="fw-bold mb-3 text-center text-md-start">
                                            Administration Tasks
                                        </h6>

                                        {tasksForCurrentStage.map(task => (
                                            <TaskInputRow
                                            key={task.id}
                                            order={order}
                                            task={task}
                                            currentOrderTaskState={currentOrderTaskState}
                                            handleTaskChange={handleTaskChange}
                                            loading={loading}
                                            alignDesktop="start"
                                            />
                                        ))}
                                        </Col>
                                    </Row>
                                  ) : (
                                    <span>No tasks defined for this stage.</span>
                                  )}
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>
          )
        )}

        {/* Save Changes Modal */}
        <Modal show={showSaveConfirmModal} onHide={handleCloseSaveConfirmModal} centered>
          <Modal.Header closeButton><Modal.Title>Confirm Save Changes</Modal.Title></Modal.Header>
          <Modal.Body>
            Are you sure you want to save all pending changes for all modified orders?
            <Alert variant="info" className="mt-3 mb-0">
              This will update every order that has unsaved changes.</Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseSaveConfirmModal}>Cancel</Button>
            <Button variant="primary" onClick={handleSaveConfirm} disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Advance Modal */}
        <Modal show={showAdvanceConfirmModal} onHide={handleCloseAdvanceConfirmModal} centered>
            <Modal.Header closeButton>
                <Modal.Title>Confirm Move to Pre-Production</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p>
                Are you sure you want to mark the Administration stage for{' '}
                <strong>
                    {orderToAdvance?.dealerName} – {orderToAdvance?.customerName}
                </strong>{' '}
                as <strong>Completed</strong> and move this order to{' '}
                <strong>Pre-Production</strong>?
                </p>

                <Alert variant="info" className="mt-3 mb-0">
                The dealer will be notified of this stage update.
                </Alert>
            </Modal.Body>

            <Modal.Footer>
                <Button
                variant="secondary"
                onClick={handleCloseAdvanceConfirmModal}
                >
                Cancel
                </Button>

                <Button
                variant="primary"
                onClick={handleAdvanceStageConfirm}
                disabled={loading}
                >
                {loading ? 'Processing…' : 'Confirm & Send'}
                </Button>
            </Modal.Footer>
        </Modal>

      </Container>
    </Container>
  );
}
